import React from 'react';
import { Link } from 'react-router-dom';
import './LCProblems.css';

const FEATURES = [
  {
    icon: '🗂️',
    title: 'Pattern-sorted',
    desc:  'Every problem tagged and grouped by its core technique — Binary Search, DP, Sliding Window and more.',
  },
  {
    icon: '🔗',
    title: 'Cross-linked',
    desc:  'Problems link directly to the full POTD walkthroughs where they exist.',
  },
  {
    icon: '⚡',
    title: 'Difficulty tiered',
    desc:  'Easy → Medium → Hard progression within each pattern so you build confidence before going deep.',
  },
  {
    icon: '🎯',
    title: 'Interview-curated',
    desc:  'Hand-picked from real FAANG, MAANG, and product company interview pools.',
  },
  {
    icon: '📊',
    title: 'Company tagged',
    desc:  'Know which companies ask which problems — focus your prep where it matters.',
  },
  {
    icon: '✅',
    title: 'Track your progress',
    desc:  'Mark problems as solved and watch your coverage grow across every pattern.',
  },
];

const PATTERNS = [
  { name: 'Sliding Window',      count: 8,  color: 'blue'   },
  { name: 'Binary Search',       count: 10, color: 'orange' },
  { name: 'Dynamic Programming', count: 14, color: 'purple' },
  { name: 'Two Pointers',        count: 8,  color: 'teal'   },
  { name: 'Graph / BFS / DFS',   count: 10, color: 'green'  },
  { name: 'Bit Manipulation',    count: 6,  color: 'gold'   },
  { name: 'Backtracking',        count: 5,  color: 'purple' },
  { name: 'Heap / Priority Q',   count: 6,  color: 'orange' },
  { name: 'Tree / Trie',         count: 8,  color: 'green'  },
  { name: 'Math & Modular',      count: 5,  color: 'blue'   },
];

const FLOATS = [
  { text: 'O(n log n)',     cls: 'lc__float lc__float--1' },
  { text: 'left++; right--', cls: 'lc__float lc__float--2' },
  { text: 'dp[i] = max(…)', cls: 'lc__float lc__float--3' },
  { text: 'graph.bfs(src)',  cls: 'lc__float lc__float--4' },
];

const total = PATTERNS.reduce((s, p) => s + p.count, 0); // 80

export default function LCProblems() {
  return (
    <main className="lc">
      <div className="lc__bg" />
      {FLOATS.map(f => (
        <div key={f.cls} className={f.cls}>{f.text}</div>
      ))}

      {/* ── Hero ── */}
      <section className="lc__hero">
        <div className="lc__eyebrow">
          <span className="lc__eyebrow-dot" />
          Coming soon
        </div>

        <h1 className="lc__h1">
          <span className="lc__h1-dim">{total}+ hand-picked</span><br />
          <span className="lc__h1-acc">LC Problems</span>
        </h1>

        <p className="lc__sub">
          A curated library of {total}+ LeetCode problems — sorted by pattern,
          annotated with the exact technique, and cross-linked to the
          POTD walkthroughs you already know. Built for focused, efficient prep.
        </p>

        <div className="lc__hero-btns">
          <Link className="lc__btn lc__btn--ghost" to="/potd">
            Browse POTD problems →
          </Link>
        </div>
      </section>

      {/* ── Pattern preview grid ── */}
      <section className="lc__sec">
        <div className="lc__sec-label">What's inside</div>
        <div className="lc__patterns">
          {PATTERNS.map((p, i) => (
            <div
              key={p.name}
              className={`lc__pat lc__pat--${p.color}`}
              style={{ animationDelay: `${0.05 + i * 0.04}s` }}
            >
              <div className="lc__pat-name">{p.name}</div>
              <div className="lc__pat-count">{p.count} problems</div>
              <div className="lc__pat-bar">
                <div
                  className="lc__pat-fill"
                  style={{ width: `${(p.count / 14) * 100}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Feature cards ── */}
      <section className="lc__sec">
        <div className="lc__sec-label">What makes it different</div>
        <div className="lc__feats">
          {FEATURES.map((f, i) => (
            <div
              key={f.title}
              className="lc__feat"
              style={{ animationDelay: `${0.1 + i * 0.07}s` }}
            >
              <div className="lc__feat-icon">{f.icon}</div>
              <div className="lc__feat-title">{f.title}</div>
              <div className="lc__feat-desc">{f.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Build progress ── */}
      <section className="lc__sec lc__sec--center">
        <div className="lc__progress-card">
          <div className="lc__prog-hd">
            <span className="lc__prog-label">Build progress</span>
            <span className="lc__prog-pct">20%</span>
          </div>
          <div className="lc__prog-track">
            <div className="lc__prog-fill" style={{ width: '20%' }} />
          </div>
          <div className="lc__prog-steps">
            {[
              { label: 'Problem curation (80+ selected)', done: true  },
              { label: 'Pattern tagging & difficulty tiers',done: false },
              { label: 'Company tag annotation',           done: false },
              { label: 'Cross-link to POTD walkthroughs',  done: false },
              { label: 'Progress tracking UI',             done: false },
              { label: 'Launch 🚀',                         done: false },
            ].map((step, i) => (
              <div key={i} className={`lc__step ${step.done ? 'lc__step--done' : ''}`}>
                <div className="lc__step-dot">
                  {step.done && <span className="lc__step-check">✓</span>}
                </div>
                <span className="lc__step-label">{step.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

    </main>
  );
}
